// Surcharge Reconciliation — the weekly expected-vs-actual run, on screen.
//
// Owner ask (2026-09-07): "bring up the surcharge thing on Tuesday, make sure
// you have a report there so we can check that out." The report is what the
// Monday 09:00 UTC task (tasks/surcharge_reconciliation.py) persists on
// surcharge_reconciliation_runs; this page reads those rows, lets an admin
// queue a fresh run before a meeting, and drills from the headline numbers to
// the order groups behind them. Tabs are the shared components/common/Tabs.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Tab, TabList, TabPanel, Tabs } from '../components/common/Tabs';
import {
  surchargeReconciliationApi,
  type OrderGroup,
  type SurchargeRunDetail,
  type SurchargeRunSummary,
} from '../services/surchargeReconciliation';
import { downloadCsv } from '../utils/csv';
import {
  activeExemptCustomers,
  CLASS_ORDER,
  classLabel,
  DEFAULT_GROUP_FILTERS,
  distinctSalespeople,
  exemptCustomersToCsv,
  filterGroups,
  groupsToCsv,
  money,
  pct,
  runLabel,
  SHORTFALL_CLASSES,
  sortGroups,
  type GroupFilters,
} from './surchargeReconciliationUtils';
import styles from './FreightAuditPage.module.css';

type TabId = 'groups' | 'customers' | 'quotes' | 'reps' | 'method';

export function SurchargeReconciliationPage() {
  const [runs, setRuns] = useState<SurchargeRunSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<SurchargeRunDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [queueNote, setQueueNote] = useState<string | null>(null);
  const [queueing, setQueueing] = useState(false);
  const [filters, setFilters] = useState<GroupFilters>(DEFAULT_GROUP_FILTERS);
  const [tab, setTab] = useState<TabId>('groups');

  const loadRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await surchargeReconciliationApi.listRuns(20);
      setRuns(list.runs);
      const completed = list.runs.find((r) => r.status === 'completed');
      const pick = selectedId ?? completed?.id ?? null;
      if (pick) {
        const d = await surchargeReconciliationApi.getRun(pick);
        setDetail(d);
        setSelectedId(pick);
      } else {
        setDetail(null);
      }
    } catch {
      setError('Failed to load the surcharge reconciliation runs.');
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    void loadRuns();
  }, [loadRuns]);

  const selectRun = async (id: string) => {
    setSelectedId(id);
    setLoading(true);
    setError(null);
    try {
      setDetail(await surchargeReconciliationApi.getRun(id));
    } catch {
      setError('Failed to load that run.');
    } finally {
      setLoading(false);
    }
  };

  const queueRun = async () => {
    setQueueing(true);
    setQueueNote(null);
    try {
      const res = await surchargeReconciliationApi.queueRun();
      setQueueNote(res.detail);
    } catch {
      setQueueNote('Could not queue a run — is the worker up?');
    } finally {
      setQueueing(false);
    }
  };

  const report = detail?.report ?? null;
  const groups = useMemo(() => (report ? sortGroups(filterGroups(report.groups, filters)) : []), [report, filters]);
  const salespeople = useMemo(() => (report ? distinctSalespeople(report.groups) : []), [report]);
  const activeExempt = useMemo(() => (report ? activeExemptCustomers(report.exempt_customers) : []), [report]);
  const classCounts = useMemo(() => {
    const m: Record<string, { groups: number; gap: string }> = {};
    for (const c of report?.class_totals ?? []) m[c.classification] = { groups: c.groups, gap: c.gap };
    return m;
  }, [report]);
  const classes = useMemo(() => {
    const known = CLASS_ORDER.filter((c) => classCounts[c]);
    const extra = Object.keys(classCounts).filter((c) => !CLASS_ORDER.includes(c)).sort();
    return [...known, ...extra];
  }, [classCounts]);

  const exemptionForegone = report
    ? Number(report.exemption_foregone_customer) + Number(report.exemption_foregone_quote)
    : 0;

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <h1>Surcharge Reconciliation</h1>
        <p>
          Expected 5% surcharge versus what was billed, per (customer, PO) group of invoices, with every
          miss classified. Runs persist every Monday 09:00 UTC; queue one here for fresh numbers.
        </p>
      </div>

      <form
        className={styles.filters}
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <label>
          Run
          <select
            aria-label="Run"
            value={selectedId ?? ''}
            onChange={(e) => void selectRun(e.target.value)}
            disabled={runs.length === 0}
          >
            {runs.length === 0 && <option value="">No runs yet</option>}
            {runs.map((r) => (
              <option key={r.id} value={r.id}>{runLabel(r)}</option>
            ))}
          </select>
        </label>
        <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => void queueRun()} disabled={queueing}>
          {queueing ? 'Queuing…' : 'Run now'}
        </button>
        <button type="button" className={styles.btn} onClick={() => void loadRuns()} disabled={loading}>
          Refresh
        </button>
        <span className={styles.spacer} />
        <button
          type="button"
          className={styles.btn}
          onClick={() => report && downloadCsv(`surcharge_groups_${report.window_start}_${report.window_end}.csv`, groupsToCsv(groups))}
          disabled={!report || groups.length === 0}
        >
          Export groups CSV
        </button>
      </form>
      {queueNote && <div className={styles.sources}>{queueNote}</div>}

      {loading && !detail && <div className={styles.sources}>Loading…</div>}
      {error && <div className={styles.error}>{error}</div>}

      {detail && detail.status !== 'completed' && (
        <div className={styles.error}>
          This run is {detail.status}{detail.error ? `: ${detail.error}` : ''}. Pick a completed run above.
        </div>
      )}

      {report && (
        <>
          <div className={styles.tiles} data-testid="headline-tiles">
            <div className={styles.tile}>
              <div className={styles.tileCount}>{money(report.expected_policy)}</div>
              <div className={styles.tileLabel}>Expected (policy)</div>
            </div>
            <div className={styles.tile}>
              <div className={styles.tileCount}>{money(report.billed_surcharge)}</div>
              <div className={styles.tileLabel}>Billed</div>
            </div>
            <div className={`${styles.tile} ${Number(report.shortfall) > 0 ? styles.tileAttention : ''}`}>
              <div className={styles.tileCount}>{money(report.shortfall)}</div>
              <div className={styles.tileLabel}>Shortfall</div>
            </div>
            <div className={styles.tile}>
              <div className={styles.tileCount}>{pct(report.capture_pct)}</div>
              <div className={styles.tileLabel}>Capture</div>
            </div>
            <div className={styles.tile}>
              <div className={styles.tileCount}>{money(exemptionForegone)}</div>
              <div className={styles.tileLabel}>Exemptions foregone</div>
            </div>
            <div className={styles.tile}>
              <div className={styles.tileCount}>{report.groups_in_scope}</div>
              <div className={styles.tileLabel}>Order groups</div>
            </div>
          </div>

          <div className={styles.sources} data-testid="window-line">
            <span>Window {report.window_start} → {report.window_end}</span>
            <span>{report.invoices_in} invoices · {report.invoices_cm} credit memos · goods {money(report.goods)}</span>
            <span>Generated {new Date(report.generated_at).toLocaleString()}</span>
            <span>
              Exemption basis: {report.exemption_basis_counts.current ?? 0} current · {report.exemption_basis_counts.history ?? 0} history · {report.exemption_basis_counts.mixed ?? 0} mixed
            </span>
          </div>

          <Tabs activeTab={tab} onTabChange={(t) => setTab(t as TabId)}>
            <TabList>
              <Tab value="groups" badge={String(report.groups.length)}>Order groups</Tab>
              <Tab value="customers" badge={String(activeExempt.length)}>Exempt customers</Tab>
              <Tab value="quotes" badge={String(report.exempt_quotes.length)}>Exempt quotes</Tab>
              <Tab value="reps" badge={String(report.by_salesperson.length)}>By rep</Tab>
              <Tab value="method" badge={String(report.caveats.length)}>Method & caveats</Tab>
            </TabList>

            <TabPanel value="groups">
              <div className={styles.tiles} data-testid="class-tiles">
                {classes.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={[
                      styles.tile,
                      filters.classification === c ? styles.tileActive : '',
                      SHORTFALL_CLASSES.has(c) && Number(classCounts[c].gap) > 0 ? styles.tileAttention : '',
                    ].join(' ')}
                    onClick={() => setFilters({ ...filters, classification: filters.classification === c ? '' : c })}
                    aria-pressed={filters.classification === c}
                  >
                    <div className={styles.tileCount}>{classCounts[c].groups}</div>
                    <div className={styles.tileLabel}>{classLabel(c)}</div>
                    <div className={styles.basis}>{SHORTFALL_CLASSES.has(c) ? `gap ${money(classCounts[c].gap)}` : 'no shortfall'}</div>
                  </button>
                ))}
              </div>

              <div className={styles.filters}>
                <label>
                  Rep
                  <select aria-label="Rep" value={filters.salesperson} onChange={(e) => setFilters({ ...filters, salesperson: e.target.value })}>
                    <option value="">All reps</option>
                    {salespeople.map((s) => (
                      <option key={s.no} value={s.no}>{s.name}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Find
                  <input aria-label="Find" placeholder="customer, PO, order, invoice…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
                </label>
                <span className={styles.spacer} />
                <span>{groups.length} of {report.groups.length} groups</span>
                {(filters.classification || filters.salesperson || filters.q) && (
                  <button type="button" className={styles.btn} onClick={() => setFilters(DEFAULT_GROUP_FILTERS)}>Clear</button>
                )}
              </div>

              <div className={styles.tableWrap}>
                <table className={styles.table} aria-label="Surcharge order groups">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>PO / orders</th>
                      <th>Invoiced</th>
                      <th>Rep</th>
                      <th className={styles.num}>Goods</th>
                      <th className={styles.num}>Expected</th>
                      <th className={styles.num}>Billed</th>
                      <th className={styles.num}>Gap</th>
                      <th>Class</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.length === 0 ? (
                      <tr>
                        <td colSpan={9} className={styles.empty}>No order groups match.</td>
                      </tr>
                    ) : (
                      groups.map((g: OrderGroup) => (
                        <tr key={`${g.customer_no}|${g.customer_po_no}`} data-testid="group-row">
                          <td>
                            {g.customer_no}
                            {g.customer_name && <div className={styles.basis}>{g.customer_name}</div>}
                            {g.customer_exempt_now && <div className={styles.basis}>exempt now</div>}
                          </td>
                          <td>
                            {g.customer_po_no || '(no PO)'}
                            <div className={styles.basis}>
                              {g.sales_order_nos.join(', ') || '—'} · inv {g.invoice_nos.join(', ')}
                            </div>
                          </td>
                          <td>
                            {g.first_invoice_date ?? '—'}
                            <div className={styles.basis}>{g.bol_scanned === null ? 'scan unknown' : g.bol_scanned ? 'scanned' : 'not scanned'}</div>
                          </td>
                          <td>{g.salesperson_name ?? g.salesperson_no ?? '—'}</td>
                          <td className={styles.num}>{money(g.goods)}</td>
                          <td className={styles.num}>{money(g.expected_policy)}</td>
                          <td className={styles.num}>
                            {money(g.billed_surcharge)}
                            {g.queued_surcharge !== null && (
                              <div className={styles.basis} title="What the sales site charged at the BOL scan (order_invoice_queue)">
                                queued {money(g.queued_surcharge)}
                              </div>
                            )}
                          </td>
                          <td className={styles.num}>{money(g.gap)}</td>
                          <td>
                            <span
                              className={[
                                styles.bucket,
                                SHORTFALL_CLASSES.has(g.classification) && Number(g.gap) > 0 ? styles.bucketAttention : '',
                                g.classification === 'collected' ? styles.bucketGood : '',
                              ].join(' ')}
                            >
                              {classLabel(g.classification)}
                            </span>
                            <div className={styles.basis}>{g.note}</div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </TabPanel>

            <TabPanel value="customers">
              <div className={styles.filters}>
                <span>
                  {activeExempt.length} exempt customers bought in the window · {report.exempt_customers.filter((r) => r.is_exempt).length} flagged exempt today
                </span>
                <span className={styles.spacer} />
                <button
                  type="button"
                  className={styles.btn}
                  onClick={() => downloadCsv(`surcharge_exempt_customers_${report.window_start}_${report.window_end}.csv`, exemptCustomersToCsv(report.exempt_customers))}
                >
                  Export full list CSV
                </button>
              </div>
              <div className={styles.tableWrap}>
                <table className={styles.table} aria-label="Exempt customers">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Rep</th>
                      <th>Exempt today</th>
                      <th className={styles.num}>Window goods</th>
                      <th className={styles.num}>Invoices</th>
                      <th className={styles.num}>Surcharge billed</th>
                      <th className={styles.num}>Foregone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeExempt.length === 0 ? (
                      <tr>
                        <td colSpan={7} className={styles.empty}>No exempt customer bought in this window.</td>
                      </tr>
                    ) : (
                      activeExempt.map((r) => (
                        <tr key={r.customer_no} data-testid="exempt-customer-row">
                          <td>
                            {r.customer_no}
                            {r.customer_name && <div className={styles.basis}>{r.customer_name}</div>}
                          </td>
                          <td>{r.salesperson_name ?? r.salesperson_no ?? '—'}</td>
                          <td>{r.is_exempt ? 'yes' : 'no (was exempt in window)'}</td>
                          <td className={styles.num}>{money(r.window_goods)}</td>
                          <td className={styles.num}>{r.window_invoice_count}</td>
                          <td className={styles.num}>{money(r.window_surcharge_billed)}</td>
                          <td className={styles.num}>{money(r.window_surcharge_foregone)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </TabPanel>

            <TabPanel value="quotes">
              <div className={styles.tableWrap}>
                <table className={styles.table} aria-label="Exempt quotes">
                  <thead>
                    <tr>
                      <th>Quote</th>
                      <th>Customer</th>
                      <th>Project</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Window invoices</th>
                      <th className={styles.num}>Quoted goods</th>
                      <th className={styles.num}>Foregone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.exempt_quotes.length === 0 ? (
                      <tr>
                        <td colSpan={8} className={styles.empty}>No exempt quote was used in this window.</td>
                      </tr>
                    ) : (
                      report.exempt_quotes.map((q) => (
                        <tr key={q.quote_id} data-testid="exempt-quote-row">
                          <td>{q.quote_id}</td>
                          <td>
                            {q.customer_no}
                            {q.customer_name && <div className={styles.basis}>{q.customer_name}</div>}
                          </td>
                          <td>{q.project_name || '—'}</td>
                          <td>{q.status ?? '—'}</td>
                          <td>{q.created_date ?? '—'}</td>
                          <td>{q.window_invoice_nos.join(', ')}</td>
                          <td className={styles.num}>{money(q.window_quoted_goods)}</td>
                          <td className={styles.num}>{money(q.window_surcharge_foregone)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </TabPanel>

            <TabPanel value="reps">
              <div className={styles.tableWrap}>
                <table className={styles.table} aria-label="By salesperson">
                  <thead>
                    <tr>
                      <th>Rep</th>
                      <th className={styles.num}>Groups</th>
                      <th className={styles.num}>Goods</th>
                      <th className={styles.num}>Expected</th>
                      <th className={styles.num}>Billed</th>
                      <th className={styles.num}>Shortfall</th>
                      <th className={styles.num}>Capture</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.by_salesperson.map((s) => (
                      <tr key={s.salesperson_no} data-testid="rep-row">
                        <td>{s.salesperson_name ?? s.salesperson_no}</td>
                        <td className={styles.num}>{s.groups}</td>
                        <td className={styles.num}>{money(s.goods)}</td>
                        <td className={styles.num}>{money(s.expected_policy)}</td>
                        <td className={styles.num}>{money(s.billed_surcharge)}</td>
                        <td className={styles.num}>{money(s.shortfall)}</td>
                        <td className={styles.num}>{pct(s.capture_pct)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabPanel>

            <TabPanel value="method">
              <div className={styles.sources}>
                <span>Program: {report.options.enabled ? 'on' : 'off'} · {report.options.percent}% · {report.options.date_start ?? '?'} → {report.options.date_end ?? 'open'}</span>
                <span>Surcharge lines billed on any invoice: {money(report.surcharge_billed_all_lines)} · unattributed {money(report.unattributed_surcharge)}</span>
                <span>Exemption history: {report.history_rows} tracked states since {report.history_earliest_valid_from ? new Date(report.history_earliest_valid_from).toLocaleDateString() : '—'}</span>
                <span data-testid="queue-line">
                  BOL-scan queue (what the site charged at scan): {report.invoice_queue_readable ? 'read for this run' : 'NOT readable in this run'}
                </span>
              </div>
              <ul className={styles.caveats} aria-label="Caveats">
                {report.caveats.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </TabPanel>
          </Tabs>
        </>
      )}

      {!loading && !error && !report && runs.length === 0 && (
        <div className={styles.sources}>No reconciliation run has been persisted yet. Click Run now.</div>
      )}
    </div>
  );
}

export default SurchargeReconciliationPage;
